import React from "react";

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORY_ORDER = ["household", "personal", "accounts"];
const CATEGORY_LABELS = {
  household: "Household Info",
  personal: "Personal Info",
  accounts: "Accounts Info",
};

// ── Icons ──────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

// ── Helper functions ───────────────────────────────────────────────────────────

const isCategoryComplete = (cat, householdPending, membersPending, accountsPending) => {
  if (cat === "household") {
    return !householdPending || householdPending.length === 0;
  }
  if (cat === "personal") {
    return (
      !membersPending ||
      membersPending.every((m) => !m.fields || m.fields.length === 0)
    );
  }
  if (cat === "accounts") {
    return (
      !accountsPending ||
      accountsPending.every((a) => !a.fields || a.fields.length === 0)
    );
  }
  return true;
};

// ── Breadcrumb ─────────────────────────────────────────────────────────────────

const Breadcrumb = ({ activeCategory, householdPending, membersPending, accountsPending }) => {
  return (
    <nav className="grid grid-cols-3 w-full" aria-label="Progress">
      {CATEGORY_ORDER.map((cat, idx) => {
        const isActive = cat === activeCategory;
        const isComplete = isCategoryComplete(cat, householdPending, membersPending, accountsPending);
        const label = CATEGORY_LABELS[cat];

        let bgClass, textClass, borderClass;
        if (isActive) {
          bgClass = "bg-gray-100";
          textClass = "text-gray-900 font-semibold";
          borderClass = "border-gray-300";
        } else {
          bgClass = "bg-white";
          textClass = "text-gray-500";
          borderClass = "border-gray-200";
        }

        const roundedClass = idx === 0
          ? "rounded-l-lg"
          : idx === CATEGORY_ORDER.length - 1
            ? "rounded-r-lg"
            : "";

        return (
          <div
            key={cat}
            className={`flex items-center justify-center gap-2 px-4 py-3 text-lg border ${borderClass} ${bgClass} ${roundedClass} ${idx > 0 ? "border-l-0" : ""}`}
          >
            {isComplete && !isActive && <CheckIcon />}
            <span className={textClass}>{label}</span>
          </div>
        );
      })}
    </nav>
  );
};

// ── Entity tabs ────────────────────────────────────────────────────────────────

const EntityTabs = ({ activeCategory, membersPending, accountsPending, activeEntityIndex, onEntityTabChange }) => {
  if (activeCategory === "household") return null;

  let entities = [];
  if (activeCategory === "personal") {
    entities = (membersPending || []).map((m) => ({ id: m.memberId, name: m.memberName }));
  } else if (activeCategory === "accounts") {
    entities = (accountsPending || []).map((a) => ({ id: a.accountId, name: a.accountName }));
  }

  if (entities.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Entity tabs">
      {entities.map((entity, idx) => {
        const isActive = idx === activeEntityIndex;
        return (
          <button
            key={entity.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onEntityTabChange(idx)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isActive
                ? "bg-emerald-100 text-emerald-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {entity.name}
          </button>
        );
      })}
    </div>
  );
};

// ── Sub-tabs ───────────────────────────────────────────────────────────────────

const SubTabs = ({ fields, activeSubTabIndex, onSubTabChange }) => {
  if (!fields || fields.length <= 1) return null;

  return (
    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Field sub-tabs">
      {fields.map((field, idx) => {
        const isActive = idx === activeSubTabIndex;
        return (
          <button
            key={field.key}
            role="tab"
            aria-selected={isActive}
            onClick={() => onSubTabChange(idx)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isActive
                ? "bg-emerald-500 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {field.label}
          </button>
        );
      })}
    </div>
  );
};

// ── Next button label ──────────────────────────────────────────────────────────

const getNextButtonLabel = ({
  activeCategory,
  activeEntityIndex,
  activeSubTabIndex,
  householdPending,
  membersPending,
  accountsPending,
  currentFields,
}) => {
  // Check for a next sub-tab in the current entity
  if (currentFields && activeSubTabIndex < currentFields.length - 1) {
    const nextField = currentFields[activeSubTabIndex + 1];
    return `Save & Next: ${nextField.label} \u2192`;
  }

  // Check for a next entity in the current category
  if (activeCategory === "personal") {
    const entities = membersPending || [];
    if (activeEntityIndex < entities.length - 1) {
      const nextEntity = entities[activeEntityIndex + 1];
      return `Save & Next: ${nextEntity.memberName} \u2192`;
    }
  }
  if (activeCategory === "accounts") {
    const entities = accountsPending || [];
    if (activeEntityIndex < entities.length - 1) {
      const nextEntity = entities[activeEntityIndex + 1];
      return `Save & Next: ${nextEntity.accountName} \u2192`;
    }
  }

  // Check next categories for pending items
  const currentCatIdx = CATEGORY_ORDER.indexOf(activeCategory);
  for (let i = currentCatIdx + 1; i < CATEGORY_ORDER.length; i++) {
    const nextCat = CATEGORY_ORDER[i];
    const hasItems = !isCategoryComplete(nextCat, householdPending, membersPending, accountsPending);
    if (hasItems) {
      return `Save & Next: ${CATEGORY_LABELS[nextCat]} \u2192`;
    }
  }

  return "Save & Finish";
};

// ── PendingDataDetail ──────────────────────────────────────────────────────────

const PendingDataDetail = ({
  activeCategory,
  activeEntityIndex,
  activeSubTabIndex,
  householdData,
  householdPending,
  membersPending,
  accountsPending,
  saving,
  onSaveAndNext,
  onSaveAndExit,
  onBackToOverview,
  onSkipToReview,
  onEntityTabChange,
  onSubTabChange,
  clientToken,
  onDataUpdate,
}) => {
  // Derive current fields based on category and entity
  const getCurrentFields = () => {
    if (activeCategory === "household") {
      return householdPending || [];
    }
    if (activeCategory === "personal") {
      const member = (membersPending || [])[activeEntityIndex];
      return member ? member.fields || [] : [];
    }
    if (activeCategory === "accounts") {
      const account = (accountsPending || [])[activeEntityIndex];
      return account ? account.fields || [] : [];
    }
    return [];
  };

  const getFieldData = () => {
    if (activeCategory === "household") return householdData;
    if (activeCategory === "personal") {
      const member = (membersPending || [])[activeEntityIndex];
      return member ? member.memberObj : null;
    }
    if (activeCategory === "accounts") {
      const account = (accountsPending || [])[activeEntityIndex];
      return account ? account.accountObj : null;
    }
    return null;
  };

  const getEntityId = () => {
    if (activeCategory === "personal") {
      const member = (membersPending || [])[activeEntityIndex];
      return member ? member.memberId : null;
    }
    if (activeCategory === "accounts") {
      const account = (accountsPending || [])[activeEntityIndex];
      return account ? account.accountId : null;
    }
    return null;
  };

  const getMissingFields = () => {
    if (!householdData?.missing_fields) return [];
    const entityId = getEntityId();
    if (activeCategory === "household") {
      return householdData.missing_fields.household || [];
    }
    if (activeCategory === "personal" && entityId) {
      const memberMissing = householdData.missing_fields.household_members || householdData.missing_fields.members;
      if (Array.isArray(memberMissing)) {
        const found = memberMissing.find((m) => m.id === entityId || m.member_id === entityId);
        return found ? found.fields || [] : [];
      }
      return memberMissing?.[entityId] || [];
    }
    if (activeCategory === "accounts" && entityId) {
      const accountMissing = householdData.missing_fields.accounts;
      if (Array.isArray(accountMissing)) {
        const found = accountMissing.find((a) => a.id === entityId || a.account_id === entityId);
        return found ? found.fields || [] : [];
      }
      return accountMissing?.[entityId] || [];
    }
    return [];
  };

  const currentFields = getCurrentFields();
  const fieldData = getFieldData();
  const entityId = getEntityId();
  const missingFields = getMissingFields();

  const nextLabel = getNextButtonLabel({
    activeCategory,
    activeEntityIndex,
    activeSubTabIndex,
    householdPending,
    membersPending,
    accountsPending,
    currentFields,
  });

  return (
    <div className="space-y-5">
      {/* Back link */}
      <button
        onClick={onBackToOverview}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-emerald-600 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to overview
      </button>

      {/* Breadcrumb */}
      <Breadcrumb
        activeCategory={activeCategory}
        householdPending={householdPending}
        membersPending={membersPending}
        accountsPending={accountsPending}
      />

      {/* Entity tabs */}
      <EntityTabs
        activeCategory={activeCategory}
        membersPending={membersPending}
        accountsPending={accountsPending}
        activeEntityIndex={activeEntityIndex}
        onEntityTabChange={onEntityTabChange}
      />

      {/* Sub-tabs */}
      {currentFields.length > 0 && (
        <SubTabs
          fields={currentFields}
          activeSubTabIndex={activeSubTabIndex}
          onSubTabChange={onSubTabChange}
        />
      )}

      {/* Form card – render all sub-tab forms but hide inactive ones so state is preserved */}
      {currentFields.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {currentFields.map((field, idx) => {
            const FieldComponent = field.component;
            if (!FieldComponent) return null;
            const isActive = idx === activeSubTabIndex;
            return (
              <div
                key={`${entityId || "household"}-${field.key}`}
                style={{ display: isActive ? "block" : "none" }}
              >
                <FieldComponent
                  householdData={householdData}
                  fieldData={fieldData}
                  missingFields={missingFields}
                  entityId={entityId}
                  section={field.section || null}
                  onSave={onSaveAndNext}
                  onSaveAndExit={onSaveAndExit}
                  saving={saving}
                  nextLabel={nextLabel}
                  clientToken={clientToken}
                  onDataUpdate={onDataUpdate}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-500 italic">No fields to display.</p>
        </div>
      )}

    </div>
  );
};

export default PendingDataDetail;
